import Image from "next/image";
import React, { useState } from "react";
import styles from "./Section5.module.scss";

const accordionContent = [
  {
    title: "Module: Security Fundamentals",
    content:
      "Learn to think like a cybersecurity professional by assessing threats and mitigating risks. Understand the differences between Artificial Intelligence (AI) and Machine Learning (ML) concepts, and their importance in cybersecurity.",
    learningList: ["CIA triad", "Machine learning", "Mitigating risks", "Artificial intelligence", "Threat detection"],
  },
  {
    title: "Module: Systems Admin Fundamentals",
    content:
      "Gain hands-on experience working with the command line and bash commands that are prominent in IT roles. You will configure and audit servers, as well as harden and secure them from malicious attacks.",
    learningList: [
      "Linux server configuration",
      "Logging",
      "Kerberos",
      "Windows server configuration",
      "Bash scripting and programming",
      "Tar, cron, and cron jobs",
      "Active directory",
    ],
  },
  {
    title: "Module: Networks and Network Security",
    content:
      "Dive into network configuration, design, protocols, and data communication. Study cryptography, network security, and web development as it relates to cybersecurity.",
    learningList: [
      "Network architecture, operations, and security",
      "Wireless security",
      "Wireshark and traffic analysis",
      "Cryptography and encryption",
      "Email security",
      "Port scanning",
    ],
  },
  {
    title: "Module: Offensive Security",
    content:
      "Gain a thorough understanding of web applications, databases, and the vulnerabilities and hardening associated with them. Dive into penetration testing using tools like Metasploit to attack and compromise networks and servers.",
    learningList: [
      "Burp suite",
      "Webshells",
      "Penetration testing execution standard",
      "Zenmap",
      "Pivoting networks",
      "XSS vulnerabilities and payloads",
      "SQL injection",
      "Searchsploit and metasploit",
      "File inclusion and command injection vulnerabilities",
    ],
  },
  {
    title: "Module: Defensive Security",
    content:
      "Dive into SIEMs and network security monitoring, practice the Incident Response framework to respond to different breaches and attacks, and learn digital forensic how to recover deleted data as part of preparing evidence for a legal case.",
    learningList: [
      "Splunk",
      "Monitoring and logging",
      "Incident response",
      "Forensics",
      "Data extraction and recovery",
    ],
  },
  {
    title: "Module: Compliance, Certs, & Careers",
    content:
      "Learn how security controls impact an organization and its employees. Focus on certification prep for the Security+ and conclude the program with a final group project presented in a mock cyber conference event.",
    learningList: [
      "Governance",
      "Compliance",
      "Business continuity planning",
      "Disaster recovery",
      "Certifications",
      "CompTIA Security +",
      "Research and technical demonstration",
    ],
  },
];

export const Section5 = function ({ scrollTo }) {
  const [accordion, setAccordion] = useState<number[]>([]);

  const toggleAccordion = (index: number) => {
    if (accordion.includes(index)) {
      setAccordion(accordion.filter((item) => item !== index));
    } else {
      setAccordion([...accordion, index]);
    }
  };

  return (
    <section className={`${styles.container} cc--component-container`}>
      <div className={styles.container_wrapper}>
        <p className="has-text-align-center">
          <Image
            decoding="async"
            width={249}
            height={65}
            src="https://media.bootcampcdn.com/global/landing-logos/comptia_logo.svg"
            alt="CompTIA logo"
          />
        </p>
        <h2 className={styles.headline}>A Proud CompTIA Partner</h2>
        <p>
          We know how important certification is for our learners; that’s why we chose CompTIA as a partner to provide
          you with a&nbsp;<strong>complimentary CertMaster test prep tool and an exam voucher</strong>&nbsp;to take the
          CompTIA certification exam of your choice: Security+, Network+, Linux+, Server+, or Cloud+.
        </p>
        <p>
          While the boot camp itself does not provide test preparation, a portion of the final module is dedicated to
          helping you learn and leverage the CertMaster Test Prep Tool for the Security+ exam to get you started
        </p>
        <div className={styles.wp_container_core_buttons_layout}>
          <a onClick={scrollTo} className={`${styles.button} wp-block-button__link`} href="#form">
            <strong>LEARN MORE</strong>
          </a>
        </div>
        <hr className={styles.border_bottom} />
      </div>
      <section className={`${styles.container_wrapper} cc--component-container`} id="curriculum_panels">
        <h2 className={styles.headline}>Innovative Curriculum. In&#8209;Demand&nbsp;Skills.</h2>
        <p>
          In 24 weeks, Columbia Engineering Cybersecurity Boot Camp will prepare you with the technical skills you need
          to protect digital environments. Through an immersive hands-on curriculum, you will not only learn fundamental
          cybersecurity methods, but also put them into action through practical exercises.
        </p>
        <p>
          The curriculum is delivered through live, online classes and high-quality coursework. Our instructors are
          fully vetted by the university and offer 1-on-1 virtual guidance to learners throughout the course.
        </p>
        <div className={styles.accordion_wrapper}>
          {accordionContent.map(({ title, content, learningList }, index) => (
            <div className={`${styles.accordion} ${accordion.includes(index) ? "open" : ""}`} key={title}>
              <a
                className={`${styles.accordion_title} ${accordion.includes(index) ? "open" : ""} panel-button`}
                onClick={() => toggleAccordion(index)}
              >
                {title}
              </a>
              <div className={styles.accordion_content_wrapper}>
                <div className={styles.accordion_content_inner}>
                  <div className={styles.accordion_content}>
                    <p>{content}</p>
                    {learningList.length > 0 && (
                      <>
                        <strong>What you will learn:</strong>
                        <ul>
                          {learningList.map((item) => (
                            <li key={item}>{item}</li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className={styles.container_wrapper} style={{ textAlign: "center" }}>
          <h3 className={styles.subheadline}>Now You’re Ready</h3>
          <p>
            By the time you’ve completed the boot camp, you will have mastered in-demand skills, built an impressive
            portfolio of projects applying your new skills in practical scenarios, and earned a valuable Award of
            Completion from Columbia Engineering.
          </p>
          <div className={styles.wp_container_core_buttons_layout}>
            <a onClick={scrollTo} className={`${styles.button} wp-block-button__link`} href="#form">
              <strong>LEARN MORE</strong>
            </a>
          </div>
        </div>
      </section>
    </section>
  );
};
